import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, tap, forkJoin, throwError } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import {
	AudioChannelDeviceSpecificEqualizerContourUpdateInput,
	NodeChangeType,
	NodeUpdateInput,
	ApolloQueryErrorMapper,
	CombiningSubscriptionManager,
	SubscriptionManagerService,
	DeviceResetScope,
	ApolloQueryError
} from '@shure/shared/angular/data-access/system-api/core';
import { mapDeviceEqualizerContourToSysApi } from '@shure/shared/angular/data-access/system-api/mappers';
import {
	ChannelDeviceSpecificEqContour,
	EqFilterType,
	Response,
	ResponseState
} from '@shure/shared/angular/data-access/system-api/models';
import { ILogger } from '@shure/shared/angular/utils/logging';
import { noMoreSubscribers, tapOnce } from '@shure/shared/typescript/rxjs';

import {
	EqualizerFilter,
	EqualizerFilterUpdate,
	ParametricEqualizerApiService,
	ParametricEqualizerChannel
} from '../../api';
import {
	EqualizerFilterMutationGQL,
	EqualizerFilterQueryGQL,
	EqualizerFilterSubscriptionsGQL,
	EqualizerFragment,
	ParametricEqualizerChannelMutationGQL,
	ParametricEqualizerChannelQueryGQL,
	ParametricEqualizerChannelSubscriptionGQL,
	ResetEqualizerFiltersMutationGQL
} from '../graphql/generated/parametric-equalizer.generated';
import { mapEqualizerFilterFromSysApi, mapPeqChannelFromSysApi } from '../mappers';

@Injectable()
export class DaiDeviceUiDspParametricEqualizerService implements ParametricEqualizerApiService {
	private readonly logger: ILogger;

	private readonly equalizerFilterSubscriptionManager: CombiningSubscriptionManager;
	private readonly parametricChannelSubscriptionManager: CombiningSubscriptionManager;

	constructor(
		logger: ILogger,
		private readonly parametricEqualizerChannelQueryGQL: ParametricEqualizerChannelQueryGQL,
		private readonly equalizerFilterQueryGQL: EqualizerFilterQueryGQL,
		private readonly equalizerFilterMutationGQL: EqualizerFilterMutationGQL,
		private readonly parametricEqualizerChannelMutationGQL: ParametricEqualizerChannelMutationGQL,
		private readonly resetEqualizerFiltersMutationGQL: ResetEqualizerFiltersMutationGQL,
		parametricEqualizerChannelSubscriptionGQL: ParametricEqualizerChannelSubscriptionGQL,
		equalizerFilterSubscriptionsGQL: EqualizerFilterSubscriptionsGQL,
		subscriptionManagerService: SubscriptionManagerService
	) {
		this.logger = logger.createScopedLogger('DaiDeviceUiDspParametricEqualizerService');

		this.equalizerFilterSubscriptionManager = new CombiningSubscriptionManager(
			{
				name: 'DaiDeviceUiDspParametricEqualizerService:EQFilter',
				changeTypes: [
					NodeChangeType.EqualizerFilterConfiguration,
					NodeChangeType.EqualizerFilterFrequency,
					NodeChangeType.EqualizerFilterGain,
					NodeChangeType.EqualizerFilterQFactor,
					NodeChangeType.EqualizerFilterBandwidth
				],
				subscriptionGql: equalizerFilterSubscriptionsGQL
			},
			logger,
			subscriptionManagerService
		);

		this.parametricChannelSubscriptionManager = new CombiningSubscriptionManager(
			{
				name: 'DaiDeviceUiDspParametricEqualizerService:ChannelEQ',
				changeTypes: [
					NodeChangeType.AudioChannelName,
					NodeChangeType.AudioChannelDeviceSpecificEqualizerContour,
					NodeChangeType.AudioChannelMicOptimization,
					NodeChangeType.AudioChannelChannelConfiguration
				],
				subscriptionGql: parametricEqualizerChannelSubscriptionGQL
			},
			logger,
			subscriptionManagerService
		);
	}

	public getChannel$(channelId: string): Observable<ParametricEqualizerChannel> {
		const queryId = uuidv4();
		this.logger.trace('getChannel$()', 'Querying peq channel', { channelId, queryId });

		return this.parametricEqualizerChannelQueryGQL
			.watch({ channelId }, { fetchPolicy: 'network-only' })
			.valueChanges.pipe(
				map((query) => {
					if (query.data?.node && 'features' in query.data.node) {
						this.logger.trace('getChannel$()', 'Query updated', { channelId });
						return mapPeqChannelFromSysApi(query.data.node);
					}

					this.logger.error('getChannel$()', 'Failed to query channel', { channelId });
					throw ApolloQueryErrorMapper.getError(query);
				}),
				tapOnce((channel) => {
					this.parametricChannelSubscriptionManager.subscribe(queryId, channel.id);
					channel.features.equalizerFilters.forEach((filter) => {
						this.equalizerFilterSubscriptionManager.subscribe(queryId, filter.id);
					});
				}),
				noMoreSubscribers(() => {
					this.logger.trace('getChannel$', 'noMoreSubscribers', { channelId, queryId });
					this.parametricChannelSubscriptionManager.unsubscribeQuery(queryId);
					this.equalizerFilterSubscriptionManager.unsubscribeQuery(queryId);
				}),
				catchError((error) => {
					return throwError(() => new ApolloQueryError(error));
				})
			);
	}

	public getEqFilter$(filterId: string): Observable<EqualizerFilter> {
		const queryId = uuidv4();
		this.logger.trace('getEqFilter$()', 'Querying peq filter', { filterId, queryId });

		return this.equalizerFilterQueryGQL.watch({ filterId }).valueChanges.pipe(
			map((query) => {
				if (query.data?.node && 'features' in query.data.node) {
					this.logger.trace('getEqFilter$()', 'Query updated', { filterId });
					return mapEqualizerFilterFromSysApi(query.data.node);
				}

				this.logger.error('getEqFilter$()', 'Failed to query filter', { filterId });
				throw ApolloQueryErrorMapper.getError(query);
			}),
			tap((filter) => this.equalizerFilterSubscriptionManager.subscribe(queryId, filter.id)),
			noMoreSubscribers(() => {
				this.logger.trace('getEqFilter$', 'noMoreSubscribers', { filterId, queryId });
				this.equalizerFilterSubscriptionManager.unsubscribeQuery(queryId);
			}),
			catchError((error) => {
				return throwError(() => new ApolloQueryError(error));
			})
		);
	}

	public setEqualizerFilterConfiguration(
		filterId: string,
		type: EqFilterType,
		enabled: boolean
	): Observable<Response<void, string>> {
		this.logger.trace('setEqualizerFilterConfiguration()', 'Updating equalizer filter configuration', {
			filterId,
			type,
			enabled
		});
		return this.updateEqualizerFilter(filterId, [{ configuration: { enabled, filterType: type } }]);
	}

	public setEqualizerFilterValues(
		filterId: string,
		_update: EqualizerFilterUpdate
	): Observable<Response<void, string>> {
		const update = Object.entries(_update).map(([key, value]) => ({ [key]: { [key]: value } }));
		this.logger.trace('setEqualizerFilterValues()', 'Updating equalizer filter values', {
			filterId,
			update
		});

		return this.updateEqualizerFilter(filterId, update);
	}

	public copyEqFilter(destFilterId: string, sourceFilter: EqualizerFilter): Observable<Response<void, string>> {
		const update = [
			{ configuration: { enabled: sourceFilter.features.enabled, filterType: sourceFilter.features.type } },
			{ frequency: { frequency: sourceFilter.features.frequency } },
			{ gain: { gain: sourceFilter.features.gain } },
			{ qFactor: { qFactor: sourceFilter.features.qFactor } }
		];
		this.logger.trace('copyEqFilter()', 'copy equalizer filter values to destination filter', {
			destFilterId,
			update
		});
		return this.updateEqualizerFilter(destFilterId, update);
	}

	public clearPEQ(channel: ParametricEqualizerChannel): Observable<Response<void, string>[]> {
		const resetPeq$ = [this.resetEqualizerFilters(channel.id)];

		if (channel.description.features.deviceEqualizerContour.supported) {
			resetPeq$.push(this.setEqContour(channel.id, ChannelDeviceSpecificEqContour.Off));
		}
		return forkJoin(resetPeq$);
	}

	public resetEqualizerFilters(channelId: string): Observable<Response<void, string>> {
		this.logger.trace('resetEqualizerFilters()', 'Resetting eq filters', { channelId });

		return this.resetEqualizerFiltersMutationGQL
			.mutate(
				{
					id: channelId,
					scope: DeviceResetScope.EqualizerFilters,
					injectKeyring: '//supplied-by-keyring-injector'
				},
				{ errorPolicy: 'all' }
			)
			.pipe(
				map((result) => {
					if (result.errors) {
						const error = result.errors[0];
						this.logger.error(
							'resetEqualizerFilters()',
							'Failed to reset eq filters due to GQL error',
							error
						);
						return { state: ResponseState.Error, error: error?.message ?? '' };
					}

					const operationResult = result.data?.resetDevice;
					if (operationResult !== undefined && 'error' in operationResult && operationResult.error !== null) {
						const error = operationResult.error;
						this.logger.error(
							'resetEqualizerFilters()',
							'Failed to reset eq filters due to operation error',
							error
						);
						return { state: ResponseState.Error, error: error.message };
					}

					return { state: ResponseState.Done };
				}),
				catchError((error: Error) => {
					this.logger.error('resetEqualizerFilters()', 'Failed to reset eq filters due to unexpected error', {
						error
					});
					return of({ state: ResponseState.Error, error: error.message });
				})
			);
	}

	public setEqContour(
		channelId: string,
		deviceEqContour: ChannelDeviceSpecificEqContour
	): Observable<Response<void, string>> {
		this.logger.trace('setEqContour()', 'Setting EQ contour', { channelId, deviceEqContour });
		return this.updateChannel(channelId, {
			deviceSpecificEqualizerContour: mapDeviceEqualizerContourToSysApi(deviceEqContour)
		});
	}

	private updateEqualizerFilter(
		filterId: string,
		_updates: Partial<EqualizerFragment['features']>[]
	): Observable<Response<void, string>> {
		const updates = _updates.map((up) => <NodeUpdateInput>{ equalizerFilters: { id: filterId, features: up } });
		this.logger.trace('updateEqualizerFilter()', 'Updating filter', { filterId, updates });
		return this.equalizerFilterMutationGQL
			.mutate({ updates, injectKeyring: '//supplied-by-keyring-injector' }, { errorPolicy: 'all' })
			.pipe(
				map((mutation) => {
					if (
						mutation.data?.updateNodes &&
						'error' in mutation.data.updateNodes[0] &&
						mutation.data.updateNodes[0].error !== null
					) {
						this.logger.error('updateEqualizerFilter()', 'Failed to update channel', {
							filterId,
							mutation,
							updates
						});
						return { state: ResponseState.Error, error: mutation.data.updateNodes[0].error?.message };
					}

					this.logger.trace('updateEqualizerFilter()', 'Done', { filterId });
					return { state: ResponseState.Done };
				}),
				catchError((error) => {
					this.logger.error('updateEqualizerFilter()', 'Failed to update channel', { error });
					return of({ state: ResponseState.Error, error });
				})
			);
	}

	private updateChannel(
		channelId: string,
		update: AudioChannelDeviceSpecificEqualizerContourUpdateInput
	): Observable<Response<void, string>> {
		this.logger.trace('updateChannel()', 'Updating channel', { channelId, update });
		return this.parametricEqualizerChannelMutationGQL
			.mutate(
				{
					updates: [
						{
							audioChannel: {
								id: channelId,
								features: {
									deviceSpecificEqualizerContour: update
								}
							}
						}
					],
					injectKeyring: '//supplied-by-keyring-injector'
				},
				{ errorPolicy: 'all' }
			)
			.pipe(
				map((result) => {
					if (
						result.data?.updateNodes &&
						'error' in result.data.updateNodes[0] &&
						result.data.updateNodes[0].error !== null
					) {
						this.logger.error('updateChannel()', 'Failed to update channel', {
							channelId,
							result
						});
						return { state: ResponseState.Error, error: result.data.updateNodes[0].error?.message };
					}

					this.logger.trace('updateChannel()', 'Done', { channelId });
					return { state: ResponseState.Done };
				}),
				catchError((error: Error) => {
					this.logger.error('updateChannel()', 'Error', { error });
					return of({ state: ResponseState.Error, error: error.message });
				})
			);
	}
}
